import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Card, Button, Dropdown, Spinner } from "react-bootstrap";
import { FiChevronLeft } from "react-icons/fi";

import LoaderModal from "../common/loader";
import { deleteProductApi, getProductEditApi, updateProductStatusApi } from "./product-api";
import { ReactComponent as ProduTyIc } from "../../assets/svg/produc_ty_ics.svg";
import { ReactComponent as ProduIdIc } from "../../assets/svg/produ_id_ics.svg";
import ProductIc from "../../assets/png/productIc.png";
import moreIcon from "../../assets/svg/more_ic.svg";
import DeleteModal from "../common/deleteModel";
import { useGlobal } from "../../GlobalContext";
import "../common/skeleton-effect.css";
import CommonSnackbar from "../common/reusable-commponent";
import {
  sanitizeInput,
  sanitizeNumericInput,
  validateUrl,
} from "../common/security-utils";

const ProductView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  // --- Data state ---
  const [product, setProduct] = useState(null);
  const [dimensionUnit, setDimensionUnit] = useState("");
  const [boxDimensionUnit, setBoxDimensionUnit] = useState("");
  const [customDimensionUnit, setCustomDimensionUnit] = useState("");
  const [singleDimension, setSingleDimension] = useState({});
  const [boxDimension, setBoxDimension] = useState({});
  const [customDimension, setCustomDimension] = useState({});

  // --- UI state ---
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  // --- Snackbar state ---
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });

  const handleSnackbarClose = useCallback(
    () => setSnackbar((prev) => ({ ...prev, show: false })),
    [],
  );

  const triggerSnackbar = useCallback((message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  }, []);

  // --- Permissions ---
  const { rolePermissionList } = useGlobal();

  const checkPermission = useCallback(
    (permission) =>
      rolePermissionList.findIndex(
        (element) =>
          element["name"] === "Product" &&
          element["menu_permission"] === true &&
          element[permission] === true,
      ) > -1,
    [rolePermissionList],
  );

  // --- Delete product ---
  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const deleteProduct = useCallback(async () => {
    if (!deleteId) {
      triggerSnackbar("Invalid product ID", false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await deleteProductApi(deleteId);
      if (response?.data?.success === true) {
        navigate(-1);
        setDeleteId(null);
        setIsOpenDelete(false);
      }
      if (response?.data) {
        triggerSnackbar(response.data.message || "Operation completed", response.data.success);
      }
    } catch {
      triggerSnackbar("An error occurred during deletion", false);
    } finally {
      setIsLoading(false);
    }
  }, [deleteId, navigate, triggerSnackbar]);

  // --- Status update ---
  const handleStatusUpdate = useCallback(
    async (newStatus) => {
      if (!product?.id) return;
      setIsStatusUpdating(true);
      try {
        const response = await updateProductStatusApi(product.id, {
          status: newStatus,
        });
        if (response?.data?.success) {
          setProduct((prev) => ({ ...prev, status: newStatus }));
          triggerSnackbar("Status updated successfully", true);
        } else {
          triggerSnackbar("Failed to update status", false);
        }
      } catch {
        triggerSnackbar("An error occurred while updating status", false);
      } finally {
        setIsStatusUpdating(false);
      }
    },
    [product?.id, triggerSnackbar],
  );

  // --- Fetch product data ---
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const res = await getProductEditApi(id);
        if (res?.data?.success) {
          setProduct(res.data.products || null);
          const single = res.data.products?.uoms?.find((u) => u.uom_name === "Single") || {};
          const box = res.data.products?.uoms?.find((u) => u.uom_name === "Box") || {};
          const custom = res.data.products?.uoms?.find((u) => u.uom_name === "Custom") || {};
          setDimensionUnit(single.measurement_type ?? "");
          setBoxDimensionUnit(box.measurement_type ?? "");
          setCustomDimensionUnit(custom.measurement_type ?? "");

          setSingleDimension({
            length: single.length ?? "",
            breadth: single.width ?? "",
            height: single.height ?? "",
            quantity: single.quantity ?? "",
            price: single.price ?? "",
          });

          setBoxDimension({
            length: box.length ?? "",
            breadth: box.width ?? "",
            height: box.height ?? "",
            quantity: box.quantity ?? "",
            price: box.price ?? "",
          });

          setCustomDimension({
            length: custom.length ?? "",
            breadth: custom.width ?? "",
            height: custom.height ?? "",
            quantity: custom.quantity ?? "",
            price: custom.price ?? "",
          });
        }
      } catch {
        // Error handled by api-service
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // --- Reusable dimension section renderer ---
  const renderDimensionSection = useCallback(
    (title, dimension, unit) => (
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <div className="text-start">
            <h6 className="fw-bold mb-3">{title}</h6>
            <div
              style={{
                display: "flex",
                gap: 20,
                alignItems: "stretch",
                flexWrap: "wrap",
              }}
            >
              {/* Left panel: stacked rows */}
              <div
                style={{
                  flex: 1,
                  minWidth: 300,
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#F7F9FB",
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)",
                }}
              >
                {[
                  { label: "LENGTH", value: dimension.length ?? 0 },
                  { label: "BREADTH", value: dimension.breadth ?? 0 },
                  { label: "HEIGHT", value: dimension.height ?? 0 },
                ].map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "14px 18px",
                      borderBottom: i < 2 ? "1px solid rgba(0,0,0,0.04)" : "none",
                      background: i % 2 === 0 ? "#F4F7FA" : "#F7F9FB",
                    }}
                  >
                    <div style={{ fontSize: 12, color: "#7D8693", fontWeight: 600 }}>
                      {row.label}
                    </div>
                    <div style={{ fontSize: 14, color: "#1F2D3D", fontWeight: 700 }}>
                      {sanitizeNumericInput(row.value)
                        ? `${sanitizeNumericInput(row.value)}${sanitizeInput(unit) ? `(${sanitizeInput(unit)})` : ""}`
                        : "-"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Right panel: quantity & price */}
              <div
                style={{
                  width: 260,
                  display: "flex",
                  flex: 1,
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    borderRadius: 12,
                    padding: "12px 16px",
                    background: "#FFFFFF",
                    border: "1px solid #E9EEF3",
                    minHeight: 64,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#7D8693", fontWeight: 600 }}>QUANTITY</div>
                  <div style={{ fontSize: 16, color: "#1F2D3D", fontWeight: 700, marginTop: 6 }}>
                    {sanitizeNumericInput(dimension.quantity) || 0}
                  </div>
                </div>

                <div
                  style={{
                    borderRadius: 12,
                    padding: "12px 16px",
                    background: "#FFFFFF",
                    border: "1px solid #E9EEF3",
                    minHeight: 64,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#7D8693", fontWeight: 600 }}>PRICE</div>
                  <div style={{ fontSize: 16, color: "#1F2D3D", fontWeight: 700, marginTop: 6 }}>
                    {sanitizeNumericInput(dimension.price) || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    ),
    [],
  );

  // --- Get safe image URL ---
  const getImageSrc = useCallback((img) => {
    const url = typeof img === "string" ? img : img.file_path || img.url || img.path;
    if (!url || typeof url !== "string") return "";
    // Allow blob/data URLs (local previews) and validated http URLs
    if (url.startsWith("blob:") || url.startsWith("data:")) return url;
    return validateUrl(url) ? url : "";
  }, []);

  // --- Loading skeleton ---
  if (loading) {
    return (
      <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <div>
          {/* Header Skeleton */}
          <div className="bg-white rounded shadow-sm p-3 mb-3 d-flex justify-content-between align-items-start">
            <div className="d-flex gap-3 align-items-center">
              <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }} />
              <div className="d-flex gap-3 align-items-center">
                <div className="skeleton" style={{ width: "64px", height: "64px", borderRadius: "50%" }} />
                <div className="d-flex flex-column gap-2">
                  <div className="skeleton skeleton-text" style={{ width: "200px", height: "20px" }} />
                  <div className="d-flex align-items-center gap-2">
                    <div className="skeleton skeleton-text-sm" style={{ width: "80px", height: "14px" }} />
                    <div className="skeleton" style={{ width: "5px", height: "5px", borderRadius: "50%" }} />
                    <div className="skeleton skeleton-button" style={{ width: "60px", height: "24px", borderRadius: "20px" }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <div className="skeleton skeleton-button" style={{ width: "100px", height: "32px" }} />
              <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
            </div>
          </div>

          <Row className="g-3 mb-3">
            <Col lg={8}>
              {/* Product Details Card Skeleton */}
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "130px" }} />
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "8px" }} />
                      <div className="skeleton skeleton-text-sm" style={{ width: "100px" }} />
                      <div className="skeleton skeleton-text" style={{ width: "120px", marginLeft: "auto" }} />
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "8px" }} />
                      <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                      <div className="skeleton skeleton-text" style={{ width: "100px", marginLeft: "auto" }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Dimension Cards Skeleton */}
              {Array.from({ length: 3 }).map((_, idx) => (
                <Card key={idx} className="border-0 shadow-sm mb-3">
                  <Card.Body>
                    <div className="skeleton skeleton-text-lg mb-3" style={{ width: idx === 0 ? "220px" : idx === 1 ? "200px" : "240px" }} />
                    <div className="d-flex gap-3 flex-wrap">
                      <div style={{ flex: 1, minWidth: "300px" }}>
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div
                            key={i}
                            className="d-flex justify-content-between align-items-center p-3"
                            style={{
                              borderBottom: i < 2 ? "1px solid #E9EEF3" : "none",
                              background: i % 2 === 0 ? "#F4F7FA" : "#F7F9FB",
                            }}
                          >
                            <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                            <div className="skeleton skeleton-text" style={{ width: "100px" }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ width: "260px", flex: 1 }} className="d-flex flex-column gap-3">
                        <div className="border rounded p-3" style={{ minHeight: "64px" }}>
                          <div className="skeleton skeleton-text-sm mb-2" style={{ width: "80px" }} />
                          <div className="skeleton skeleton-text" style={{ width: "60px" }} />
                        </div>
                        <div className="border rounded p-3" style={{ minHeight: "64px" }}>
                          <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                          <div className="skeleton skeleton-text" style={{ width: "80px" }} />
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </Col>

            <Col lg={4}>
              {/* Product Images Card Skeleton */}
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "130px" }} />
                  <div className="d-flex flex-wrap gap-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="skeleton" style={{ width: "72px", height: "72px", borderRadius: "8px" }} />
                    ))}
                  </div>
                </Card.Body>
              </Card>

              {/* Description Card Skeleton */}
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "100px" }} />
                  <div className="d-flex flex-column gap-2">
                    <div className="skeleton skeleton-text" style={{ width: "100%" }} />
                    <div className="skeleton skeleton-text" style={{ width: "90%" }} />
                    <div className="skeleton skeleton-text" style={{ width: "80%" }} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <p>Product not found.</p>
      </div>
    );
  }

  const images = product.product_attachments || product.images || product.product_images || [];

  return (
    <>
      <LoaderModal show={isLoading} />
      <DeleteModal
        title={"product"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteProduct}
      />

      <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <div className="mt-2">
          {/* --- Header --- */}
          <div className="bg-white rounded shadow-sm p-3 mb-3 d-flex justify-content-between align-items-start">
            <div className="d-flex gap-3 align-items-center">
              <Button
                variant="light"
                className="rounded-circle d-flex align-items-center justify-content-center border"
                style={{ width: 40, height: 40 }}
                onClick={() => navigate(-1)}
              >
                <FiChevronLeft size={20} />
              </Button>

              <div className="d-flex gap-3 align-items-center text-start">
                <div className="rounded d-flex align-items-center justify-content-center" style={{ width: 64, height: 64 }}>
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "100px",
                      border: "1px solid #EBEBEB",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={ProductIc}
                      alt="product"
                      style={{ width: "54px", height: "54px", objectFit: "contain" }}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {/* Title */}
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#1F2D3D" }}>
                    {sanitizeInput(product.item_name ?? "")}
                  </div>

                  {/* Sub info row */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "14px", color: "#6B7280" }}>
                      {sanitizeInput(product.criteria ?? "")}
                    </span>

                    <span
                      style={{
                        width: "5px",
                        height: "5px",
                        borderRadius: "50%",
                        backgroundColor: "#9CA3AF",
                        display: "inline-block",
                      }}
                    />

                    <span
                      style={{
                        backgroundColor: "#EEF4FF",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        color: "#3563E9",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sanitizeInput(product.type ?? "")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex flex-column align-items-end gap-2">
              <div className="d-flex align-items-center gap-2">
                {checkPermission("edit_permission") && (
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="white"
                      className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2 fs-13"
                      style={{ color: product.status ? "#198754" : "#dc3545" }}
                    >
                      {isStatusUpdating ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <span
                            className={`rounded-circle ${product.status ? "bg-success" : "bg-danger"}`}
                            style={{ width: "8px", height: "8px" }}
                          />
                          {product.status ? "Active" : "Inactive"}
                        </>
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleStatusUpdate(true)}>Active</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleStatusUpdate(false)}>Inactive</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}

                <div className="d-flex flex-row flex-grow-1 align-items-end justify-content-end gap-2 ps-3">
                  <div onClick={(e) => e.stopPropagation()}>
                    {(checkPermission("edit_permission") || checkPermission("delete_permission")) && (
                      <Dropdown>
                        <Dropdown.Toggle
                          bsPrefix="custom-toggle"
                          variant="light"
                          id="dropdown-sort"
                          className="d-flex align-items-center justify-content-between rounded-5 bg-white"
                          style={{ width: "36px", height: "36px", border: "1px solid #EBEBEB" }}
                        >
                          <span className="d-flex align-items-center px-1 gap-2">
                            <img src={moreIcon} alt="more" />
                          </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            border: "1px solid #EBEBEB",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          {checkPermission("edit_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={() => {
                                navigate("/product/create", {
                                  state: { id },
                                });
                              }}
                            >
                              Edit
                            </Dropdown.Item>
                          )}
                          {checkPermission("delete_permission") && (
                            <Dropdown.Item
                              className="fs-14 fw-500 textcolor"
                              onClick={() => {
                                setDeleteId(id);
                              }}
                            >
                              Delete
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- Content --- */}
          <Row className="g-3 mb-3">
            <Col lg={8}>
              {/* Product Details */}
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body style={{ padding: 0 }}>
                  <div style={{ borderRadius: 12, overflow: "hidden", background: "#FFFFFF" }}>
                    <div
                      className="text-start"
                      style={{ padding: "18px 20px", borderBottom: "1px solid transparent" }}
                    >
                      <div className="fs-16 fw-700">Product details</div>
                    </div>

                    <div style={{ padding: "18px 20px 22px 20px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {/* Product ID */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: "20px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: "227px" }}>
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <ProduIdIc />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#7D8693" }}>
                              Product Id
                            </div>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2D3D" }}>
                            {sanitizeInput(product.item_code ?? "") || "-"}
                          </div>
                        </div>

                        {/* Weight */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: "20px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: "227px" }}>
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <ProduTyIc />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "#7D8693" }}>
                              Weight
                            </div>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2D3D" }}>
                            {sanitizeNumericInput(product.weight) || "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Dimension sections */}
              {renderDimensionSection("Single Product Dimension", singleDimension, dimensionUnit)}
              {renderDimensionSection("Box Product Dimension", boxDimension, boxDimensionUnit)}
              {renderDimensionSection("Custom Product Dimension", customDimension, customDimensionUnit)}
            </Col>

            <Col lg={4}>
              {/* Product Images */}
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body className="text-start">
                  <h6 className="fw-bold mb-3">Product Images</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {images && images.length ? (
                      images.map((img, idx) => {
                        const src = getImageSrc(img);
                        return (
                          <div
                            key={idx}
                            style={{
                              width: 72,
                              height: 72,
                              borderRadius: 8,
                              overflow: "hidden",
                              background: "#F7F9FB",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {src ? (
                              <img
                                className="img-fluid"
                                src={src}
                                alt={`product-${idx}`}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            ) : (
                              <div className="text-muted fs-12">Invalid</div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-muted">No images</div>
                    )}
                  </div>
                </Card.Body>
              </Card>

              {/* Description */}
              <Card className="border-0 shadow-sm">
                <Card.Body className="text-start">
                  <h6 className="fw-bold mb-3">Description</h6>
                  <div className="text-muted">{sanitizeInput(product.details ?? "") || "-"}</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* ---------- Snackbar ---------- */}
      <CommonSnackbar
        show={snackbar.show}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        isSuccess={snackbar.status}
      />
    </>
  );
};

export default ProductView;
